import { useState } from 'react';
import { FileDown, ShieldCheck, Loader2 } from 'lucide-react';
import { Card, Button, Input, FormField, useToast, BankConfirmationLetter, pdf } from '@afrilink/ui';
import { siteConfig } from '@afrilink/config';
import { useAuth } from '@/shared/contexts/AuthContext';

/**
 * BankConfirmationLetterCard — downloadable "Confirmation of Banking Details"
 * letter, on /sales.
 *
 * WHY: an agent quoting a prospect over WhatsApp eventually has to send an
 * account number, and a bare account number in a chat is indistinguishable from
 * the invoice-redirection fraud that is endemic here. Agents were solving that
 * by improvising — screenshots, forwarded messages, retyping. This gives them
 * one letterheaded PDF that says these are the only details, that they never
 * change, and who to phone to check.
 *
 * DELIBERATELY UNGATED, unlike PartnerLinkCard. That card is manager-only and
 * relies on `sales_company` RLS returning zero rows for an agent. This one is
 * for every sales agent (owner direction 2026-08-12), and it needs no RLS
 * reasoning at all: the letter is built entirely from `siteConfig` plus the
 * signed-in user's own name. It reads nothing from the database, so there is no
 * row to leak and nothing to scope.
 *
 * Generated in the browser on click rather than served as a static asset, so
 * the letter carries today's date and the agent's own name and never goes
 * stale in a bucket.
 */
export function BankConfirmationLetterCard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [addressee, setAddressee] = useState('');
  const [busy, setBusy] = useState(false);

  const agentName = (user?.user_metadata?.full_name as string | undefined)?.trim() || null;

  async function download() {
    setBusy(true);
    try {
      const blob = await pdf(
        <BankConfirmationLetter
          company={{
            companyName: siteConfig.name,
            legalName: siteConfig.legalName,
            registrationNumber: siteConfig.registration,
            address: [
              siteConfig.address.street,
              siteConfig.address.locality,
              siteConfig.address.city,
              siteConfig.address.postalCode,
            ]
              .filter(Boolean)
              .join(', '),
            phone: siteConfig.phoneDisplay,
            email: siteConfig.supportEmail,
            website: siteConfig.url,
          }}
          bank={{
            name: siteConfig.bank.name,
            accountName: siteConfig.bank.accountName,
            accountNumber: siteConfig.bank.accountNumber,
            branchCode: siteConfig.bank.branchCode,
            accountType: siteConfig.bank.accountType,
            swiftCode: siteConfig.bank.swiftCode || undefined,
          }}
          issuer={{
            name: agentName,
            email: user?.email ?? null,
          }}
          addressee={addressee.trim() || null}
          verifyUrl={`${siteConfig.url}/contact`}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Addressee in the filename so an agent working several deals in a day
      // does not end up with banking-details(3).pdf and no idea who it is for.
      const who = addressee.trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
      a.download = who
        ? `SightHost-Banking-Details-${who}.pdf`
        : 'SightHost-Banking-Details.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        tone: 'success',
        title: 'Letter downloaded',
        body: 'Attach it to your email or send it on WhatsApp.',
      });
    } catch (err) {
      console.error('Bank confirmation letter generation failed', err);
      toast({
        tone: 'danger',
        title: 'Could not generate the letter',
        body: 'Please try again. If it keeps failing, contact support.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-4 sm:p-5">
      <div className="flex flex-col gap-1">
        <h2 className="inline-flex items-center gap-2 text-base font-semibold">
          <ShieldCheck className="h-4 w-4 text-primary" aria-hidden />
          Banking details letter
        </h2>
        <p className="text-sm text-muted-foreground">
          A letterheaded PDF confirming our bank account, for customers who want
          proof before they pay. It carries today&apos;s date and your name, and
          warns the customer that our details never change — send this instead of
          typing the account number into a chat.
        </p>
      </div>

      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
        <FormField
          label="Address it to (optional)"
          htmlFor="bank-letter-addressee"
          className="flex-1"
        >
          <Input
            id="bank-letter-addressee"
            value={addressee}
            onChange={(e) => setAddressee(e.target.value)}
            placeholder="e.g. Mr T Nkosi, or leave blank"
            autoComplete="off"
          />
        </FormField>

        <Button onClick={download} disabled={busy} className="sm:mb-0.5">
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <FileDown className="h-4 w-4" aria-hidden />
          )}
          {busy ? 'Preparing…' : 'Download letter'}
        </Button>
      </div>
    </Card>
  );
}
